import type { Currency, Locale } from '@backmarket/http-api'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { IntlMessageFormat } from 'intl-messageformat'

import type { I18nDefinition, I18nTranslations, I18nValues } from '../../types'

interface Arguments {
  translations: I18nTranslations
  locale: Locale
  showTranslationKeys?: boolean
  showDefaultMessage?: boolean
  currencyCode: Currency
}

export default function createMessageFormatter({
  translations,
  locale,
  showTranslationKeys = false,
  showDefaultMessage = false,
  currencyCode,
}: Arguments) {
  return (definition: I18nDefinition, values: I18nValues = {}) => {
    /**
     * In some cases, when definitions are computed from dynamic values,
     * we may end up in situations where nothing is passed to this function.
     */
    if (!definition) {
      // eslint-disable-next-line no-console
      console.error('[nuxt-module-i18n] Missing translation definition', {
        locale,
      })

      /**
       * Instead of crashing the application, let's gracefully handle the error
       * by displaying nothing to our end customers.
       */
      return null
    }

    try {
      /**
       * Do not display those warning in unit tests
       * as we don't have translations there.
       */
      const translation = translations[definition.id]

      if (!showDefaultMessage && !translation && !isTestEnv()) {
        if (translation === undefined) {
          // eslint-disable-next-line no-console
          console.error('[nuxt-module-i18n] Missing translation key', {
            definition,
            locale,
          })
        } else if (translation === '') {
          // eslint-disable-next-line no-console
          console.error('[nuxt-module-i18n] Missing translation value', {
            definition,
            locale,
          })
        }
      }

      if (showTranslationKeys) {
        return definition.id
      }

      const raw =
        !showDefaultMessage && translation
          ? translation
          : definition.defaultMessage

      const escaped = raw.replace(/{(\w*), html}/g, "'{$1}'")

      const message = new IntlMessageFormat(escaped, toBcp47Locale(locale), {
        number: {
          currency: {
            style: 'currency',
            currency: currencyCode,
          },
        },
      })

      return message.format(values) as string
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[nuxt-module-i18n] - createMessageFormatter', { error })

      // If the key does not have a translation nor a default message.
      return null
    }
  }
}
